import { ArrowDown, LoaderCircle } from "lucide-react";
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import type { GalleryItem, GalleryPage } from "../shared";
import { DetailModal } from "./DetailModal";

function itemIdFromPath(): string | null {
  const match = window.location.pathname.match(/^\/p\/([0-9a-f-]+)$/u);
  return match?.[1] ?? null;
}

function preloadImage(source: string): Promise<void> {
  const image = new Image();
  image.decoding = "async";
  image.src = source;
  if (image.complete) return image.decode().catch(() => undefined);
  return new Promise((resolve) => {
    image.addEventListener("load", () => resolve(), { once: true });
    image.addEventListener("error", () => resolve(), { once: true });
  });
}

export function Gallery() {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<GalleryItem | null>(null);
  const [columnCount, setColumnCount] = useState(4);
  const sentinel = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<Promise<GalleryPage | null> | null>(null);
  const navigationRef = useRef(0);
  const itemsRef = useRef<GalleryItem[]>([]);
  const selectedRef = useRef<GalleryItem | null>(null);

  const selectItem = useCallback((item: GalleryItem | null) => {
    selectedRef.current = item;
    setSelected(item);
  }, []);

  const load = useCallback((nextCursor?: string | null): Promise<GalleryPage | null> => {
    if (loadingRef.current) return loadingRef.current;
    const request = (async () => {
      setLoading(true);
      setError(null);
      try {
        const query = nextCursor ? `?cursor=${encodeURIComponent(nextCursor)}` : "";
        const page = await api<GalleryPage>(`/api/gallery${query}`);
        const nextItems = nextCursor ? [...itemsRef.current, ...page.items] : page.items;
        itemsRef.current = nextItems;
        setItems(nextItems);
        setCursor(page.nextCursor);
        return page;
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Could not load the gallery.");
        return null;
      } finally {
        loadingRef.current = null;
        setLoading(false);
      }
    })();
    loadingRef.current = request;
    return request;
  }, []);

  useEffect(() => {
    void load(null);
  }, [load]);

  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      setColumnCount(width <= 380 ? 1 : width <= 820 ? 2 : width <= 1180 ? 3 : 4);
    };
    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  const effectiveColumnCount = Math.min(columnCount, Math.max(items.length, 1));
  const columns = useMemo(() => {
    const next = Array.from({ length: effectiveColumnCount }, () => [] as GalleryItem[]);
    const heights = Array.from({ length: effectiveColumnCount }, () => 0);
    for (const item of items) {
      let shortest = 0;
      for (let index = 1; index < heights.length; index += 1) {
        if (heights[index] < heights[shortest]) shortest = index;
      }
      next[shortest].push(item);
      heights[shortest] += item.height / item.width + (item.title ? 0.16 : 0.05);
    }
    return next;
  }, [effectiveColumnCount, items]);

  useEffect(() => {
    const openFromPath = async () => {
      const id = itemIdFromPath();
      if (!id) {
        selectItem(null);
        return;
      }
      const local = items.find((item) => item.id === id);
      if (local) {
        selectItem(local);
        return;
      }
      try {
        selectItem(await api<GalleryItem>(`/api/gallery/${id}`));
      } catch {
        window.history.replaceState({}, "", "/");
        selectItem(null);
      }
    };
    void openFromPath();
    window.addEventListener("popstate", openFromPath);
    return () => window.removeEventListener("popstate", openFromPath);
  }, [items, selectItem]);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !cursor) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void load(cursor);
      },
      { rootMargin: "600px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [cursor, load]);

  const open = (item: GalleryItem) => {
    navigationRef.current += 1;
    window.history.pushState({}, "", `/p/${item.id}`);
    selectItem(item);
  };

  const close = useCallback(() => {
    navigationRef.current += 1;
    if (itemIdFromPath()) window.history.pushState({}, "", "/");
    selectItem(null);
  }, [selectItem]);

  const navigate = useCallback(async (direction: "previous" | "next") => {
    const navigation = ++navigationRef.current;
    const current = selectedRef.current;
    if (!current) return;
    const currentIndex = itemsRef.current.findIndex((item) => item.id === current.id);
    if (currentIndex < 0) return;
    const offset = direction === "previous" ? -1 : 1;
    let target: GalleryItem | undefined = itemsRef.current[currentIndex + offset];
    if (!target && direction === "next" && cursor) {
      const nextPage = await load(cursor);
      target = nextPage?.items[0];
    }
    if (!target || navigation !== navigationRef.current) return;
    window.history.replaceState({}, "", `/p/${target.id}`);
    selectItem(target);
  }, [cursor, load, selectItem]);

  const handleNavigate = useCallback((direction: "previous" | "next") => {
    void navigate(direction);
  }, [navigate]);

  useEffect(() => {
    if (!selected) return;
    const currentIndex = items.findIndex((item) => item.id === selected.id);
    if (currentIndex < 0) return;
    for (const neighbor of [items[currentIndex - 1], items[currentIndex + 1]]) {
      if (neighbor) void preloadImage(neighbor.imageUrl);
    }
  }, [items, selected]);

  return (
    <main>
      <section className="gallery-section page-shell" aria-label="Prompt gallery">
        <div className="section-heading">
          <span>The archive</span>
          <span>{items.length ? `${items.length}${cursor ? "+" : ""} works` : ""}</span>
        </div>
        {items.length > 0 && (
          <div className="masonry-grid" style={{ "--columns": effectiveColumnCount } as CSSProperties}>
            {columns.map((column, columnIndex) => (
              <div className="masonry-column" key={columnIndex}>
                {column.map((item) => (
                  <article className="gallery-card" key={item.id}>
                    <button type="button" onClick={() => open(item)} aria-label={`Open ${item.title || "untitled artwork"}`}>
                      <div className="card-image-wrap" style={{ aspectRatio: `${item.width} / ${item.height}` }}>
                        <img src={item.thumbnailUrl} alt={item.title || "Gallery artwork"} loading="lazy" decoding="async" />
                        {item.tags.length > 0 && (
                          <div className="card-tags" aria-hidden="true">
                            {item.tags.map((tag) => <span key={tag}>{tag}</span>)}
                          </div>
                        )}
                      </div>
                      {item.title && <h2>{item.title}</h2>}
                    </button>
                  </article>
                ))}
              </div>
            ))}
          </div>
        )}
        {!loading && items.length === 0 && !error && (
          <div className="empty-state">
            <p className="eyebrow">The archive is ready</p>
            <h2>The first image is waiting to be published.</h2>
            <a href="/admin">Open the studio</a>
          </div>
        )}
        {error && (
          <div className="load-message" role="alert">
            <span>{error}</span>
            <button type="button" onClick={() => void load(items.length ? cursor : null)}>Try again</button>
          </div>
        )}
        <div ref={sentinel} className="load-sentinel" aria-live="polite">
          {loading && <><LoaderCircle size={18} className="spin" /> Loading the archive</>}
          {!loading && cursor && <><ArrowDown size={18} /> Keep scrolling</>}
        </div>
      </section>
      {selected && <DetailModal item={selected} onClose={close} onNavigate={handleNavigate} />}
    </main>
  );
}
